import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { FirebaseError } from 'firebase/app';
import { db } from '../config/firebase';
import {
  SystemConfig,
  SystemConfigFirestore,
  RegistrationConfig,
  ContentConfig,
  EmailConfig,
  SecurityConfig,
  FeatureFlags,
  TimelineConfig,
  LookAndFeelConfig,
  ChangeTrackingConfig,
  MetadataConfig,
} from '../types/admin';
import { auditLogService } from './auditLogService';

/**
 * System Configuration Service
 * Handles application-wide settings and configuration management
 *
 * This service provides methods for:
 * - Getting and updating system configuration
 * - Managing registration settings
 * - Managing content settings
 * - Managing email notification settings
 * - Managing security settings
 * - Managing feature flags
 * - Resetting to default configuration
 *
 * Configuration is stored as a singleton document in Firestore with ID 'app-config'.
 * All configuration changes are logged to the audit log.
 */
export class SystemConfigService {
  private static readonly CONFIG_DOC_ID = 'app-config';
  private static readonly PUBLIC_CONFIG_DOC_ID = 'public-config';
  private static readonly CONFIG_VERSION = '1.0.0';

  /**
   * Get default system configuration
   * @returns Default configuration object
   */
  private static getDefaultConfig(): Omit<SystemConfig, 'id' | 'updatedAt' | 'updatedBy'> {
    return {
      registration: {
        enabled: true,
        requireFamilyCode: false,
        autoApprove: false,
        requireEmailVerification: true,
        defaultRole: 'viewer',
        allowedDomains: [],
      },
      content: {
        // Photo settings
        maxPhotoSizeMB: 10,
        allowedPhotoTypes: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
        photoUploadLimit: 10,
        maxPhotosPerPerson: 100,
        enablePhotoTagging: true,
        requirePhotoApproval: false,
        enableComments: true,
        requireCommentApproval: false,

        // Document upload settings
        maxDocumentSizeMB: 10,
        allowedDocumentTypes: ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'tiff'],
        documentUploadLimit: 5,
        maxDocumentsPerPerson: 200,
        requireDocumentApproval: false,
        enableDocumentTranscription: false,

        // Memory settings
        maxMemoryAudioSizeMB: 50,
        maxMemoryVideoSizeMB: 200,
        allowedMemoryAudioTypes: ['mp3', 'm4a', 'wav'],
        allowedMemoryVideoTypes: ['mp4', 'mov'],
        maxMemoriesPerPerson: 0, // 0 = unlimited
        requireMemoryApproval: false,
        maxMemoryTextLength: 50000,

        // Notes settings
        enablePrivateNotes: true,
        enablePublicNotes: true,
        requirePublicNoteApproval: false,
        maxNotesPerPerson: 0, // 0 = unlimited
        maxNoteLength: 10000,

        // Comment settings (for profile comments tab)
        enableCommentThreading: true,
        maxCommentThreadDepth: 3,
        enableCommentReactions: true,
        enableCommentMentions: true,
        commentEditWindowMinutes: 15,

        // Mention settings (for all content types)
        enableMessageMentions: true,
        enableNoteMentions: true,
        enableMemoryMentions: true,
        enableFeatureRequestMentions: true,
        maxMentionsPerContent: 10,
        searchMinCharacters: 1,
        searchMaxResults: 10,
        searchDebounceMs: 300,

        // Research notes settings
        enableResearchNotes: true,
        maxResearchNotesPerPerson: 0, // 0 = unlimited
        requireResearchNoteApproval: false,
        enableSourceCitations: true,
      },
      email: {
        enableNotifications: true,
        sendWelcomeEmail: true,
        sendApprovalNotification: true,
        sendRejectionNotification: true,
        sendContentNotifications: true,
        notifyOnApproval: true,
        notifyOnRejection: true,
        notifyOnComment: true,
        notifyOnPhotoTag: true,
        notifyOnBirthday: true,

        // New profile tabs notifications
        notifyOnDocumentUpload: false,
        notifyOnMemoryPublished: false,
        notifyOnPublicNoteAdded: false,
        notifyOnCommentMention: true, // **CRITICAL** - User should know when mentioned
        notifyOnCommentReply: false,

        // Additional notification triggers (Phase 1 - in-app, Phase 4 - email)
        notifyOnNoteAdded: false,
        notifyOnResearchNoteAssigned: true,
        notifyOnContentApproved: true,
        notifyOnContentRejected: true,
        notifyOnRoleChanged: true,
        notifyOnAccountSuspended: true,

        // Notification system settings
        notificationRetentionDays: 90,
        maxNotificationsPerUser: 500,
        notifyOnResearchNoteUpdate: false,

        fromEmail: 'noreply@martzgenealogy.com',
        fromName: 'Martz Genealogy',
      },
      security: {
        passwordMinLength: 8,
        requireStrongPassword: true,
        passwordRequireUppercase: true,
        passwordRequireLowercase: true,
        passwordRequireNumbers: true,
        passwordRequireSymbols: false,
        sessionTimeoutMinutes: 480, // 8 hours
        maxLoginAttempts: 5,
        lockoutDurationMinutes: 30,
      },
      features: {
        enableFamilyTree: true,
        enablePhotoGallery: true,
        enableTimeline: true,
        enableComments: true,
        enableRelationships: true,
        enableDemographics: true,
        enableExport: true,
        enableImport: false, // GEDCOM import not implemented yet
        enableSearch: true,
        enableReports: true,

        // New profile tabs feature flags
        enableDocumentsTab: true,
        enableMemoriesTab: true,
        enableNotesTab: true,
        enableCommentsTab: true,
        enableResearchTab: true,

        // Master timeline feature flag (Phase 3.3)
        enableMasterTimeline: true,
      },
      timeline: {
        // Performance settings
        maxEventsToDisplay: 0, // 0 = unlimited
        virtualizationThreshold: 100,
        enableGenerationCalculation: true,

        // Default view settings
        defaultViewMode: 'chronological',
        defaultSortOrder: 'asc',
        defaultDateRange: 'all',

        // Export settings
        allowExports: true,
        allowPdfExport: true,
        allowCsvExport: true,
        allowGedcomExport: true,
        maxExportEvents: 10000,

        // Sharing settings
        enableShareLinks: true,
        shareLinksIncludeFilters: true,

        // Display settings
        showEventIcons: true,
        showPersonPhotos: true,
        showStatistics: true,

        // Filter defaults
        enablePersonFilter: true,
        enableEventTypeFilter: true,
        enableDateRangeFilter: true,
        enableGenerationFilter: true,
      },
      maintenance: {
        enabled: false,
      },
      lookAndFeel: {
        navigationStyle: 'current',
        defaultThemeVariant: 'heritage',
        defaultThemeMode: 'light',
      },
      changeTracking: {
        // Core settings
        enableChangeTracking: true,
        enableSourceTracking: true,

        // Visibility settings
        showChangeHistoryOnProfiles: true,
        showChangeHistoryToViewers: false,
        showChangeHistoryToContributors: true,

        // Per-tab visibility
        showDocumentChanges: true,
        showMemoryChanges: true,
        showNoteChanges: true,
        showCommentChanges: true,
        showResearchChanges: true,

        // Display settings
        defaultView: 'collapsed',
        recentChangesCount: 5,

        // Master changes page
        enableMasterChangesPage: true,
      },
      metadata: {
        showLastUpdated: false,                   // Disabled by default
        showLastUpdatedTo: ['admin', 'editor'],   // Admin and editor can see
        showLastUpdatedBy: true,                  // Show author name
        showToggleButton: true,                   // Toggle button visible
        showToggleButtonTo: ['admin'],            // Only admin can toggle
        displayFormat: 'short',                   // "2h ago" format
      },
      version: this.CONFIG_VERSION,
    };
  }

  /**
   * Get current system configuration
   * If no configuration exists, creates and returns default configuration
   * For non-admin users, attempts to read from public config first
   * @returns Current system configuration
   */
  static async getConfig(): Promise<SystemConfig> {
    try {
      const configRef = doc(db, 'systemConfig', this.CONFIG_DOC_ID);
      const configDoc = await getDoc(configRef);

      if (!configDoc.exists()) {
        // Create default configuration
        console.log('No system configuration found, creating default...');
        return await this.createDefaultConfig();
      }

      const data = configDoc.data() as SystemConfigFirestore;

      return {
        id: configDoc.id as 'app-config',
        registration: data.registration,
        content: data.content,
        email: data.email,
        security: data.security,
        features: data.features,
        timeline: data.timeline || this.getDefaultConfig().timeline,
        maintenance: data.maintenance || {
          enabled: false,
          message: undefined,
          estimatedEndTime: undefined,
        },
        lookAndFeel: data.lookAndFeel || {
          navigationStyle: 'current',
          defaultThemeVariant: 'heritage',
          defaultThemeMode: 'light',
        },
        changeTracking: data.changeTracking || this.getDefaultConfig().changeTracking,
        metadata: data.metadata || this.getDefaultConfig().metadata,
        updatedAt: data.updatedAt?.toDate(),
        updatedBy: data.updatedBy,
        updatedByDisplayName: data.updatedByDisplayName,
        version: data.version,
      };
    } catch (error) {
      // Check if it's a permission error
      if (error instanceof FirebaseError && error.code === 'permission-denied') {
        console.log('Permission denied for systemConfig, falling back to publicConfig');
        return await this.getPublicConfig();
      }

      console.error('Error getting system config:', error);
      throw new Error('Failed to retrieve system configuration');
    }
  }

  /**
   * Get public configuration (accessible to all authenticated users)
   * Contains non-sensitive settings like navigation, themes, feature flags
   * @returns Public configuration with defaults for missing fields
   */
  static async getPublicConfig(): Promise<SystemConfig> {
    try {
      const publicConfigRef = doc(db, 'publicConfig', this.PUBLIC_CONFIG_DOC_ID);
      const publicConfigDoc = await getDoc(publicConfigRef);

      const defaults = this.getDefaultConfig();

      if (!publicConfigDoc.exists()) {
        // Return default config with public-safe values
        console.log('No public configuration found, using defaults');
        return {
          id: 'app-config',
          ...defaults,
          updatedAt: new Date(),
          updatedBy: 'system',
        };
      }

      const data = publicConfigDoc.data();

      // Return config with only public-safe fields
      return {
        id: 'app-config',
        registration: defaults.registration, // Use defaults for sensitive fields
        content: defaults.content,
        email: defaults.email,
        security: defaults.security,
        features: data.features || defaults.features,
        timeline: data.timeline || defaults.timeline,
        maintenance: data.maintenance || defaults.maintenance,
        lookAndFeel: data.lookAndFeel || defaults.lookAndFeel,
        changeTracking: defaults.changeTracking,
        metadata: defaults.metadata,
        updatedAt: data.updatedAt?.toDate() || new Date(),
        updatedBy: data.updatedBy || 'system',
        updatedByDisplayName: data.updatedByDisplayName,
        version: data.version || this.CONFIG_VERSION,
      };
    } catch (error) {
      console.error('Error getting public config:', error);
      // Fallback to defaults on any error
      const defaults = this.getDefaultConfig();
      return {
        id: 'app-config',
        ...defaults,
        updatedAt: new Date(),
        updatedBy: 'system',
      };
    }
  }

  /**
   * Create default system configuration
   * @returns Newly created default configuration
   */
  private static async createDefaultConfig(): Promise<SystemConfig> {
    try {
      const defaultConfig = this.getDefaultConfig();
      const configRef = doc(db, 'systemConfig', this.CONFIG_DOC_ID);

      const configData: Omit<SystemConfigFirestore, 'updatedAt'> & { updatedAt: any } = {
        id: 'app-config',
        ...defaultConfig,
        updatedAt: serverTimestamp(),
        updatedBy: 'system',
      };

      await setDoc(configRef, configData);

      return {
        id: 'app-config',
        ...defaultConfig,
        updatedAt: new Date(),
        updatedBy: 'system',
      };
    } catch (error) {
      console.error('Error creating default config:', error);
      throw new Error('Failed to create default configuration');
    }
  }

  /**
   * Sync public config with system config
   * Updates the public config collection with non-sensitive fields from system config
   * @param config - Current system configuration
   */
  private static async syncPublicConfig(config: SystemConfig): Promise<void> {
    try {
      const publicConfigRef = doc(db, 'publicConfig', this.PUBLIC_CONFIG_DOC_ID);

      // Only sync public-safe fields
      const publicData = {
        features: config.features,
        timeline: config.timeline,
        maintenance: config.maintenance,
        lookAndFeel: config.lookAndFeel,
        updatedAt: serverTimestamp(),
        updatedBy: config.updatedBy,
        updatedByDisplayName: config.updatedByDisplayName,
        version: config.version,
      };

      await setDoc(publicConfigRef, publicData, { merge: true });
      console.log('Public config synced successfully');
    } catch (error) {
      console.error('Error syncing public config:', error);
      // Don't throw - this is non-critical
    }
  }

  /**
   * Update system configuration
   * @param updates - Partial configuration updates
   * @param updatedBy - User ID performing the update
   * @param updatedByDisplayName - Display name of user performing the update
   * @returns Updated configuration
   */
  static async updateConfig(
    updates: Partial<Omit<SystemConfig, 'id' | 'updatedAt' | 'updatedBy' | 'updatedByDisplayName' | 'version'>>,
    updatedBy: string,
    updatedByDisplayName?: string
  ): Promise<SystemConfig> {
    try {
      const configRef = doc(db, 'systemConfig', this.CONFIG_DOC_ID);

      // Get current config to compare changes
      const currentConfig = await this.getConfig();

      // Prepare update data
      const updateData: any = {
        ...updates,
        updatedAt: serverTimestamp(),
        updatedBy,
      };

      if (updatedByDisplayName) {
        updateData.updatedByDisplayName = updatedByDisplayName;
      }

      await updateDoc(configRef, updateData);

      // Get the updated config
      const updatedConfig = await this.getConfig();

      // Sync public config for non-admin users
      await this.syncPublicConfig(updatedConfig);

      // Log the configuration change
      await auditLogService.logAction({
        userId: updatedBy,
        action: 'settings_updated',
        details: {
          changes: this.getChangeSummary(currentConfig, updates),
          updatedFields: Object.keys(updates),
        },
      });

      console.log('System configuration updated by', updatedBy);

      // Return updated config
      return updatedConfig;
    } catch (error) {
      console.error('Error updating system config:', error);
      throw new Error('Failed to update system configuration');
    }
  }

  /**
   * Update registration configuration
   * @param updates - Registration config updates
   * @param updatedBy - User ID performing the update
   * @returns Updated configuration
   */
  static async updateRegistrationConfig(
    updates: Partial<RegistrationConfig>,
    updatedBy: string
  ): Promise<SystemConfig> {
    const currentConfig = await this.getConfig();
    return await this.updateConfig(
      {
        registration: {
          ...currentConfig.registration,
          ...updates,
        },
      },
      updatedBy
    );
  }

  /**
   * Update content configuration
   * @param updates - Content config updates
   * @param updatedBy - User ID performing the update
   * @returns Updated configuration
   */
  static async updateContentConfig(
    updates: Partial<ContentConfig>,
    updatedBy: string
  ): Promise<SystemConfig> {
    const currentConfig = await this.getConfig();
    return await this.updateConfig(
      {
        content: {
          ...currentConfig.content,
          ...updates,
        },
      },
      updatedBy
    );
  }

  /**
   * Update email configuration
   * @param updates - Email config updates
   * @param updatedBy - User ID performing the update
   * @returns Updated configuration
   */
  static async updateEmailConfig(
    updates: Partial<EmailConfig>,
    updatedBy: string
  ): Promise<SystemConfig> {
    const currentConfig = await this.getConfig();
    return await this.updateConfig(
      {
        email: {
          ...currentConfig.email,
          ...updates,
        },
      },
      updatedBy
    );
  }

  /**
   * Update security configuration
   * @param updates - Security config updates
   * @param updatedBy - User ID performing the update
   * @returns Updated configuration
   */
  static async updateSecurityConfig(
    updates: Partial<SecurityConfig>,
    updatedBy: string
  ): Promise<SystemConfig> {
    const currentConfig = await this.getConfig();
    return await this.updateConfig(
      {
        security: {
          ...currentConfig.security,
          ...updates,
        },
      },
      updatedBy
    );
  }

  /**
   * Update feature flags
   * @param updates - Feature flag updates
   * @param updatedBy - User ID performing the update
   * @returns Updated configuration
   */
  static async updateFeatureFlags(
    updates: Partial<FeatureFlags>,
    updatedBy: string
  ): Promise<SystemConfig> {
    const currentConfig = await this.getConfig();
    return await this.updateConfig(
      {
        features: {
          ...currentConfig.features,
          ...updates,
        },
      },
      updatedBy
    );
  }

  /**
   * Update look and feel configuration
   * @param updates - Look and feel config updates
   * @param updatedBy - User ID performing the update
   * @returns Updated configuration
   */
  static async updateLookAndFeelConfig(
    updates: Partial<LookAndFeelConfig>,
    updatedBy: string
  ): Promise<SystemConfig> {
    const currentConfig = await this.getConfig();
    return await this.updateConfig(
      {
        lookAndFeel: {
          ...currentConfig.lookAndFeel,
          ...updates,
        },
      },
      updatedBy
    );
  }

  /**
   * Enable or disable maintenance mode
   * @param enabled - Whether to enable maintenance mode
   * @param message - Optional message to display during maintenance
   * @param updatedBy - User ID performing the update
   * @returns Updated configuration
   */
  static async setMaintenanceMode(
    enabled: boolean,
    message: string | undefined,
    updatedBy: string
  ): Promise<SystemConfig> {
    const maintenanceUpdate: any = {
      enabled,
    };

    // Only include message if it's defined (Firestore doesn't allow undefined)
    if (message !== undefined) {
      maintenanceUpdate.message = message;
    }

    return await this.updateConfig(
      {
        maintenance: maintenanceUpdate,
      },
      updatedBy
    );
  }

  /**
   * Reset configuration to defaults
   * @param updatedBy - User ID performing the reset
   * @returns Reset configuration
   */
  static async resetToDefaults(updatedBy: string): Promise<SystemConfig> {
    try {
      const defaultConfig = this.getDefaultConfig();
      const configRef = doc(db, 'systemConfig', this.CONFIG_DOC_ID);

      const configData: Omit<SystemConfigFirestore, 'updatedAt'> & { updatedAt: any } = {
        id: 'app-config',
        ...defaultConfig,
        updatedAt: serverTimestamp(),
        updatedBy,
      };

      await setDoc(configRef, configData);

      // Log the reset
      await auditLogService.logAction({
        userId: updatedBy,
        action: 'settings_updated',
        details: {
          action: 'reset_to_defaults',
          message: 'System configuration reset to default values',
        },
      });

      console.log('System configuration reset to defaults by', updatedBy);

      return {
        id: 'app-config',
        ...defaultConfig,
        updatedAt: new Date(),
        updatedBy,
      };
    } catch (error) {
      console.error('Error resetting config to defaults:', error);
      throw new Error('Failed to reset configuration to defaults');
    }
  }

  /**
   * Validate configuration values
   * @param config - Configuration to validate
   * @returns Validation result with errors if any
   */
  static validateConfig(
    config: Partial<SystemConfig>
  ): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate registration config
    if (config.registration) {
      if (
        config.registration.allowedDomains &&
        !Array.isArray(config.registration.allowedDomains)
      ) {
        errors.push('Registration: allowedDomains must be an array');
      }
    }

    // Validate content config
    if (config.content) {
      if (
        config.content.maxPhotoSizeMB !== undefined &&
        (config.content.maxPhotoSizeMB <= 0 || config.content.maxPhotoSizeMB > 100)
      ) {
        errors.push('Content: maxPhotoSizeMB must be between 1 and 100');
      }

      if (
        config.content.photoUploadLimit !== undefined &&
        (config.content.photoUploadLimit <= 0 || config.content.photoUploadLimit > 100)
      ) {
        errors.push('Content: photoUploadLimit must be between 1 and 100');
      }

      if (
        config.content.maxPhotosPerPerson !== undefined &&
        config.content.maxPhotosPerPerson <= 0
      ) {
        errors.push('Content: maxPhotosPerPerson must be greater than 0');
      }

      if (
        config.content.allowedPhotoTypes &&
        !Array.isArray(config.content.allowedPhotoTypes)
      ) {
        errors.push('Content: allowedPhotoTypes must be an array');
      }
    }

    // Validate security config
    if (config.security) {
      if (
        config.security.passwordMinLength !== undefined &&
        (config.security.passwordMinLength < 6 || config.security.passwordMinLength > 128)
      ) {
        errors.push('Security: passwordMinLength must be between 6 and 128');
      }

      if (
        config.security.sessionTimeoutMinutes !== undefined &&
        config.security.sessionTimeoutMinutes <= 0
      ) {
        errors.push('Security: sessionTimeoutMinutes must be greater than 0');
      }

      if (
        config.security.maxLoginAttempts !== undefined &&
        config.security.maxLoginAttempts <= 0
      ) {
        errors.push('Security: maxLoginAttempts must be greater than 0');
      }

      if (
        config.security.lockoutDurationMinutes !== undefined &&
        config.security.lockoutDurationMinutes <= 0
      ) {
        errors.push('Security: lockoutDurationMinutes must be greater than 0');
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Get a summary of configuration changes for audit log
   * @param oldConfig - Previous configuration
   * @param updates - Configuration updates
   * @returns Human-readable change summary
   */
  private static getChangeSummary(
    oldConfig: SystemConfig,
    updates: Partial<Omit<SystemConfig, 'id' | 'updatedAt' | 'updatedBy' | 'version'>>
  ): string {
    const changes: string[] = [];

    if (updates.registration) {
      changes.push('Registration settings updated');
    }

    if (updates.content) {
      changes.push('Content settings updated');
    }

    if (updates.email) {
      changes.push('Email settings updated');
    }

    if (updates.security) {
      changes.push('Security settings updated');
    }

    if (updates.features) {
      changes.push('Feature flags updated');
    }

    if (updates.maintenance?.enabled !== undefined) {
      changes.push(
        updates.maintenance.enabled ? 'Maintenance mode enabled' : 'Maintenance mode disabled'
      );
    }

    return changes.length > 0 ? changes.join(', ') : 'Configuration updated';
  }

  /**
   * Export configuration as JSON
   * @returns Configuration as JSON string
   */
  static async exportConfig(): Promise<string> {
    try {
      const config = await this.getConfig();

      // Remove sensitive fields if needed
      const exportData = {
        ...config,
        version: this.CONFIG_VERSION,
        exportedAt: new Date().toISOString(),
      };

      return JSON.stringify(exportData, null, 2);
    } catch (error) {
      console.error('Error exporting config:', error);
      throw new Error('Failed to export configuration');
    }
  }

  /**
   * Import configuration from JSON
   * @param jsonString - Configuration JSON string
   * @param importedBy - User ID performing the import
   * @returns Imported configuration
   */
  static async importConfig(jsonString: string, importedBy: string): Promise<SystemConfig> {
    try {
      const importData = JSON.parse(jsonString);

      // Validate the imported data
      const validation = this.validateConfig(importData);
      if (!validation.valid) {
        throw new Error(`Invalid configuration: ${validation.errors.join(', ')}`);
      }

      // Remove metadata fields
      const { id, updatedAt, updatedBy, version, exportedAt, ...configToImport } = importData;

      // Update configuration
      return await this.updateConfig(configToImport, importedBy);
    } catch (error: any) {
      console.error('Error importing config:', error);
      throw new Error(`Failed to import configuration: ${error.message}`);
    }
  }

  /**
   * Check if a feature is enabled
   * @param feature - Feature name to check
   * @returns Whether the feature is enabled
   */
  static async isFeatureEnabled(feature: keyof FeatureFlags): Promise<boolean> {
    try {
      const config = await this.getConfig();
      return config.features[feature] || false;
    } catch (error) {
      console.error('Error checking feature flag:', error);
      return false; // Default to false on error
    }
  }

  /**
   * Check if maintenance mode is active
   * @returns Whether maintenance mode is enabled and optional message
   */
  static async getMaintenanceStatus(): Promise<{
    enabled: boolean;
    message?: string;
  }> {
    try {
      const config = await this.getConfig();
      return {
        enabled: config.maintenance.enabled,
        message: config.maintenance.message,
      };
    } catch (error) {
      console.error('Error checking maintenance status:', error);
      return { enabled: false };
    }
  }
}

// Export singleton instance
export const systemConfigService = SystemConfigService;
